import { useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useReviews } from "@/hooks/use-reviews";
import { InstagramCommentCard } from "./comments/InstagramCommentCard";
import { Loader2 } from "lucide-react";
import { Review } from "@/types/review";

interface CommentsListProps {
  bookId: number;
  onReplyClick: (comment: Review) => void;
}

export function CommentsList({ bookId, onReplyClick }: CommentsListProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const observerTarget = useRef<HTMLDivElement>(null);

  const {
    reviews,
    isLoading,
    loadMoreReviews,
    hasNextPage,
    isFetchingNextPage,
    likeReview,
    addReply: addReplyMutation,
    deleteReview: deleteReviewMutation,
    deleteReply: deleteReplyMutation,
    userReview,
    totalCount,
    isLikingReview,
    isAddingReply,
    isDeletingReview,
  } = useReviews(bookId, true, true); // enableFetch = true

  // Wrap mutations to match ReviewCard's expected signature
  const handleLike = (reviewId: string) => likeReview(reviewId);
  const handleReply = (reviewId: string, content: string) => addReplyMutation({ reviewId, content });
  const handleDelete = (reviewId: string) => deleteReviewMutation(reviewId);
  const handleDeleteReply = (replyId: string) => deleteReplyMutation(replyId);

  // Infinite scroll with Intersection Observer
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];
      if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
        loadMoreReviews();
      }
    },
    [hasNextPage, isFetchingNextPage, loadMoreReviews]
  );

  useEffect(() => {
    const element = observerTarget.current;
    const option = {
      root: scrollContainerRef.current,
      rootMargin: "200px", // Load more when 200px from bottom
      threshold: 0,
    };

    const observer = new IntersectionObserver(handleObserver, option);
    if (element) observer.observe(element);

    return () => {
      if (element) observer.unobserve(element);
    };
  }, [handleObserver]);

  // Stagger animation for comments
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 15 },
    show: { 
      opacity: 1, 
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 25,
      },
    },
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 text-white/40 animate-spin" />
      </div>
    );
  }

  if (reviews.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-6">
        <p className="text-white/70 text-lg mb-2">No comments yet</p>
        <p className="text-white/50 text-sm">Be the first to share your thoughts!</p>
      </div>
    );
  }

  return (
    <div 
      ref={scrollContainerRef}
      className="h-full overflow-y-auto px-2 py-4"
      style={{ scrollBehavior: "smooth" }}
    >
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="show"
        className="space-y-2 pb-4"
      >
        <AnimatePresence mode="popLayout">
          {reviews.map((review) => (
            <InstagramCommentCard
              key={review.id}
              comment={review}
              onLike={handleLike}
              onReplyClick={onReplyClick}
              onDeleteComment={handleDelete}
              onDeleteReply={handleDeleteReply}
              isLiking={isLikingReview}
            />
          ))}
        </AnimatePresence>

        {/* Loading Indicator for Infinite Scroll */}
        {isFetchingNextPage && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-6 h-6 text-white/40 animate-spin" />
          </div>
        )}

        {/* Intersection Observer Target */}
        <div ref={observerTarget} className="h-4" />
      </motion.div>

      {/* End of Comments Indicator */}
      {!hasNextPage && reviews.length > 0 && (
        <div className="text-center py-4 text-white/50 text-sm">
          {totalCount ? `All ${totalCount} comments loaded` : 'No more comments'}
        </div>
      )}
    </div>
  );
}
